import { ISocialMediaPost, Industry } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { isTemplateNew } from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../models/hooks/useLambda';
import { ITemplateCard, TemplateScope, TemplateType } from '../../../../viewmodels/AppViewModels';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { ButtonBase } from '../../Button';
import { PopoverType, TinyPopover } from '../../TinyPopover';
import { CampaignExpiryHint } from '../../campaigns/CampaignExpiryHint';
import '../../richContent/RichContentDocumentEditor/placeholders.tinymce.css';
import {
	BasicImagePreview,
	BasicVideoPreview,
	InstagramImagePreview,
	PreviewSize,
} from '../../socialMedia/PreviewSocialMediaPost/presentation';
import { ImageMissingIcon } from '../../svgs/icons/ImageMissingIcon';
import { SocialMediaIndicatorIcon } from '../../svgs/icons/SocialMediaIndicatorIcon';
import { CampaignTemplateCardCta } from '../CampaignTemplateCard/presentation';
import { TemplateKeywords } from '../TemplateKeywords';
import { SocialCampaignTemplateCardSummary } from './presentation';
import { styleSheet } from './styles';

interface Props {
	card: ITemplateCard | ISocialMediaPost;
	className?: string;
	ctaText?: string;
	isFeatured?: boolean;
	onCtaClicked?(): void;
	onAssociatedTemplateClicked?(e: React.MouseEvent<Element>): void;
	styles?: StyleDeclarationValue[];
	contextMenu?: React.ReactNode;
	industry?: Industry;
	invalidateTemplates?: () => void;
}

export function SocialCampaignTemplateCard(props: Props) {
	const { card, className, ctaText, onCtaClicked, onAssociatedTemplateClicked, styles } = props;
	const userSession = useUserSession();
	const [showAssociatedPopover, , setShowAssociatedPopover] = useLambda(false);

	const onAssociatedIndicatorClicked = (e: React.MouseEvent<SVGSVGElement>) => {
		setShowAssociatedPopover(false)();
		onAssociatedTemplateClicked?.(e);
	};

	const tCard = card as ITemplateCard;
	const expirationDate = tCard?.schedule?.expirationDate ? moment(tCard?.schedule?.expirationDate) : undefined;
	const hasAssociatedTemplateOrCampaign = tCard.associatedTemplates?.length > 0;

	// Keywords logic
	const creatorId = tCard.creator?.id;
	const currentUserId = userSession.user.id;
	const impersonationUserId = userSession.impersonationContext?.user?.id;
	const isAdmin = userSession.userRole.toLocaleLowerCase().includes('admin');

	// Show keywords if they exist, but make read-only if user is not creator and not admin
	const isCreator = creatorId && creatorId === currentUserId;
	const isImpersonatingCreator = creatorId && creatorId === impersonationUserId;
	const canEditKeywords = isCreator || isImpersonatingCreator || isAdmin;
	const hasKeywords = tCard?.keywords && tCard.keywords.length > 0;
	const isSystemTemplate = tCard.scope === TemplateScope.System;

	// Show keywords if user can edit them
	const showKeywords =
		!isSystemTemplate && props.industry && props.invalidateTemplates && tCard.id && (canEditKeywords || hasKeywords);

	return (
		<div
			className={`${css(styleSheet.container, styleSheet.containerPost, ...(styles || []))}
			social-campaign-template-card
			${className || ''}`}
		>
			<div className={css(bs.flex)}>
				<div
					className={`campaign-template-card-name ${css(
						!hasAssociatedTemplateOrCampaign && styleSheet.name,
						styleSheet.postName,
						baseStyleSheet.truncateText
					)}`}
				>
					<div
						className={css(
							bs.bgBackground,
							bs.flex,
							bs.gap2,
							bs.itemsCenter,
							bs.justifyBetween,
							bs.boxBorder,
							bs.border0,
							bs.borderB,
							bs.borderSolid,
							bs.borderNameCircles,
							bs.p4,
							bs.wFull
						)}
					>
						<div className={css(bs.truncateText, bs.flex, bs.itemsCenter, bs.gap2)}>
							<ButtonBase className={css(bs.truncateText, bs.fontBold, bs.textTitles)} onClick={onCtaClicked}>
								{card.name || 'Custom Post'}
							</ButtonBase>
							{expirationDate ? <CampaignExpiryHint expirationDate={expirationDate} /> : null}
							{isTemplateNew(tCard) ? <span className={css(styleSheet.newItem)}>(New!)</span> : null}
						</div>
						{props.contextMenu ?? null}
					</div>
				</div>
				{hasAssociatedTemplateOrCampaign && (
					<TinyPopover
						align='end'
						anchor={
							<SocialMediaIndicatorIcon
								className={css(
									bs.absolute,
									bs.top0,
									bs.right0,
									bs.cursorPointer,
									showAssociatedPopover && styleSheet.flagHighlight
								)}
								fillColor='#89C947'
								onClick={onAssociatedIndicatorClicked}
								onMouseEnter={setShowAssociatedPopover(true)}
								onMouseLeave={setShowAssociatedPopover(false)}
							/>
						}
						dismissOnOutsideAction={true}
						isOpen={showAssociatedPopover}
						placement={['top', 'left']}
						type={PopoverType.white}
					>
						<div className={css(styleSheet.popover)}>Also available as an email campaign.</div>
					</TinyPopover>
				)}
			</div>
			<div className={css(styleSheet.summaryPadding)}>
				<SocialCampaignTemplateCardSummary ctaClicked={onCtaClicked} content={card?.content} bigScreenCutoff={120} />
			</div>
			{card?.attachments?.length > 0 ? (
				card?.attachments?.[0].mimeType?.startsWith('video/') ? (
					<BasicVideoPreview
						previewSize={PreviewSize.Sm}
						postImages={card?.attachments}
						onCtaClick={onCtaClicked}
						controls={false}
					/>
				) : card?.attachments?.length > 1 ? (
					<InstagramImagePreview
						previewSize={PreviewSize.Sm}
						postImages={card?.attachments}
						onCtaClick={onCtaClicked}
					/>
				) : (
					<BasicImagePreview previewSize={PreviewSize.Sm} postImages={card?.attachments} onCtaClick={onCtaClicked} />
				)
			) : (
				<p className={css(styleSheet.imageContainer)}>
					<ImageMissingIcon />
				</p>
			)}
			<div className={css(styleSheet.buttonStack)}>
				<CampaignTemplateCardCta
					ctaText={ctaText}
					templateType={TemplateType.SocialMediaPost}
					card={card as ITemplateCard}
					onCtaClick={onCtaClicked}
				/>
				{showKeywords ? (
					<TemplateKeywords
						keywords={tCard.keywords || []}
						templateId={tCard.id}
						industry={props.industry}
						invalidateTemplates={props.invalidateTemplates}
						readOnly={!canEditKeywords}
					/>
				) : null}
				{tCard.scope === TemplateScope.Account && tCard.creator?.id !== userSession.user?.id
					? (() => {
							let sharedByName = 'UNKNOWN';
							if (tCard.creator?.firstName || tCard.creator?.lastName) {
								sharedByName = `${tCard.creator?.firstName} ${tCard.creator?.lastName}`;
							}
							return <div className={css(styleSheet.sharedByFooter)}>Shared by {sharedByName}</div>;
						})()
					: null}
			</div>
		</div>
	);
}
